/**
 * Shared "pick a destination requirement" dialog.
 *
 * Used by:
 *   - OrphanPhotosCard (recategorize stranded buckets after template swap)
 *   - PhotoReviewDialog "Move to different section" (single-photo move)
 *   - PhotosPanel bulk-select mode (multi-select move)
 *
 * Contract:
 *   - `photoIds` is the exact list of photo ids to move. The dialog
 *     is agnostic to how the caller collected them.
 *   - `progress` is the project's `photo_progress` — used to render
 *     the current template's categories/items as the picker options.
 *   - `currentKeys` (optional) is a set of requirement_keys that should
 *     be shown as disabled/greyed with a "· current" suffix. Callers
 *     that want a specific "you are here" indicator (the single-photo
 *     Move flow) can pass a Set of one key; bulk callers pass an empty
 *     Set or omit it.
 *   - `onMoved(response)` fires on success — dialog closes itself.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PhotoDestinationDialog({
  projectId,
  photoIds,
  progress,
  currentKeys,
  title = "Move photos",
  description = "Pick a destination requirement in the current template. The photos' category + requirement_key will be re-tagged; the photos themselves stay in storage.",
  open,
  onOpenChange,
  onMoved,
}) {
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  // Reset picked when the dialog opens with a fresh photo set — stale
  // selection from a prior open would silently confirm to the wrong
  // requirement.
  useEffect(() => {
    if (open) setPicked(null);
  }, [open, photoIds]);

  const disabledKeys = currentKeys instanceof Set ? currentKeys : new Set(currentKeys || []);
  const count = photoIds?.length || 0;

  const submit = async () => {
    if (!picked || count === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/photos/recategorize`, {
        photo_ids: photoIds,
        category: picked.category,
        requirement_key: picked.key,
      });
      toast.success(
        `Moved ${data.modified} photo${data.modified === 1 ? "" : "s"} → ${picked.label}`
      );
      onMoved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="photo-destination-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight">
            {title.replace("{n}", String(count))}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {(progress?.categories || []).map((cat) => (
            <div key={cat.name} data-testid={`dest-cat-${cat.name}`}>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-display font-bold mb-1.5">
                {cat.name}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {cat.items.map((it) => {
                  const isCurrent = disabledKeys.has(it.key);
                  const selected = picked?.key === it.key;
                  return (
                    <button
                      key={it.key}
                      type="button"
                      disabled={isCurrent}
                      onClick={() => setPicked({ category: cat.name, key: it.key, label: it.label })}
                      className={`text-left p-2 rounded border text-xs ${
                        isCurrent
                          ? "bg-slate-900 border-slate-800 text-slate-600 cursor-not-allowed"
                          : selected
                            ? "bg-blue-950/40 border-blue-600 text-blue-100"
                            : "bg-slate-950 border-slate-700 hover:border-slate-500 text-slate-200"
                      }`}
                      data-testid={`dest-item-${it.key}`}
                    >
                      <div className="font-medium">
                        {it.label}{isCurrent && " · current"}
                      </div>
                      {it.count > 0 && !isCurrent && (
                        <div className="text-[10px] text-slate-500 mt-0.5 font-mono">
                          {it.count} already here
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {(progress?.categories || []).length === 0 && (
            <div className="text-sm text-slate-400 italic">
              No template categories available. Set a photo template on the project first.
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !picked || count === 0}
            className="bg-red-700 hover:bg-red-600 font-display tracking-wider"
            data-testid="dest-confirm"
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {picked
              ? `MOVE ${count} → "${picked.label}"`
              : "PICK A DESTINATION"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
